import { createContext, useContext, useEffect, useMemo, useState, Fragment } from 'react';
import { translations } from './translations';

const I18nContext = createContext(null);
const STORAGE_KEY = 'scc.lang';

const getInitialLang = () => {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'en' || saved === 'fr') return saved;
  const browserLang = (navigator.language || '').toLowerCase();
  return browserLang.startsWith('fr') ? 'fr' : 'en';
};

const resolvePath = (obj, path) => {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(getInitialLang);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(() => {
    const dict = translations[lang] || translations.en;
    const t = (key, fallback) => {
      const result = resolvePath(dict, key);
      if (result !== undefined && result !== null) return result;
      // Fallback to English then to the provided fallback / key
      const enResult = resolvePath(translations.en, key);
      if (enResult !== undefined && enResult !== null) return enResult;
      return fallback ?? key;
    };
    return { lang, setLang, t, toggleLang: () => setLang((l) => (l === 'en' ? 'fr' : 'en')) };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used inside <I18nProvider>');
  return ctx;
}

// Renders a string with **bold** markers as <strong> tags.
export function RichText({ text, className }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </span>
  );
}
